import React, { useCallback, useEffect, useState } from 'react';
import { X, Send, Loader2, CornerUpRight, Trash2, ShieldAlert, Flag } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { IdentityToggle } from './IdentityToggle';
import ReportCommentModal from './ReportCommentModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const timeAgo = (iso) => {
  if (!iso) return '';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const initials = (n) => (n || '?').split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase();

/**
 * CircleCommentSheet — bottom-sheet modal that shows the full comment thread
 * for a community reflection and lets the current user post free-text comments
 * or reply to individual comments. Threaded replies fire a push to the parent
 * comment author; new top-level comments fire a push to the reflection author.
 */
const CircleCommentSheet = ({ reflectionId, open, onClose, onCommentAdded }) => {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [draft,    setDraft]    = useState('');
  const [replyTo,  setReplyTo]  = useState(null); // {id, userName}
  const [anon,     setAnon]     = useState(false);
  const [posting,  setPosting]  = useState(false);
  const [reportingId, setReportingId] = useState(null);

  const authHeaders = useCallback(() => {
    const t = localStorage.getItem('rbr_token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, []);

  const load = useCallback(async () => {
    if (!reflectionId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/community/reflections/${reflectionId}/comments`, {
        headers: { ...authHeaders() },
      });
      const body = await res.json();
      setComments(body.comments || []);
    } catch {
      toast.error('Could not load replies');
    } finally {
      setLoading(false);
    }
  }, [reflectionId, authHeaders]);

  useEffect(() => {
    if (open) {
      setDraft('');
      setReplyTo(null);
      setAnon(false);
      load();
    }
  }, [open, load]);

  const submit = async () => {
    const text = draft.trim();
    if (!text) return;
    if (!user) { toast('Sign in to reply'); return; }
    setPosting(true);
    try {
      const res = await fetch(`${API_URL}/api/community/reflections/${reflectionId}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          content: text,
          anonymous: anon,
          parentCommentId: replyTo?.id || null,
        }),
      });
      const body = await res.json();
      if (!res.ok) {
        toast.error(body.detail || 'Could not post — try again');
      } else {
        setComments((prev) => [...prev, { ...body.comment, mine: true, canDelete: true }]);
        setDraft('');
        setReplyTo(null);
        setAnon(false);
        onCommentAdded?.();
        toast.success(replyTo ? 'Reply sent 💛' : 'Comment posted');
      }
    } catch {
      toast.error('Network error — try again');
    } finally {
      setPosting(false);
    }
  };

  const deleteComment = async (commentId, isModAction) => {
    const label = isModAction ? 'Delete this reply as a moderator?' : 'Delete this reply?';
    if (!window.confirm(label)) return;
    try {
      const res = await fetch(`${API_URL}/api/comments/${commentId}`, {
        method: 'DELETE',
        headers: { ...authHeaders() },
      });
      if (res.ok) {
        setComments((prev) => prev.filter((c) => c.id !== commentId));
        toast.success(isModAction ? 'Comment removed 🛡️' : 'Comment deleted');
      } else {
        const body = await res.json().catch(() => ({}));
        toast.error(body.detail || 'Could not delete comment');
      }
    } catch {
      toast.error('Network error — try again');
    }
  };

  if (!open) return null;

  // Group replies under their parents. Simple single-level threading — good
  // enough for community reflections; nested-forever adds no product value.
  const topLevel = comments.filter((c) => !c.parentCommentId);
  const repliesByParent = comments.reduce((acc, c) => {
    if (c.parentCommentId) {
      (acc[c.parentCommentId] = acc[c.parentCommentId] || []).push(c);
    }
    return acc;
  }, {});

  return (
    <div
      className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="circle-comment-sheet"
    >
      <div className="bg-[#FFFCF3] w-full max-w-[520px] max-h-[85vh] rounded-t-3xl sm:rounded-3xl shadow-2xl border border-[#D6AD60]/30 flex flex-col animate-fade-up">
        {/* Header */}
        <div className="px-5 py-3 border-b border-stone-200 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.24em] font-semibold" style={{ color: '#D6AD60' }}>
              Replies
            </p>
            <p className="text-[11px] text-stone-500 mt-0.5">
              {comments.length} {comments.length === 1 ? 'reply' : 'replies'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            data-testid="circle-comments-close"
            className="w-9 h-9 rounded-full flex items-center justify-center text-stone-500 hover:text-stone-800 hover:bg-stone-100"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Thread */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <div className="text-center py-8 text-stone-400 text-sm flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> loading…
            </div>
          ) : topLevel.length === 0 ? (
            <div className="text-center py-8 text-stone-400 text-sm">
              No replies yet. Be the first.
            </div>
          ) : (
            <ul className="space-y-4">
              {topLevel.map((c) => {
                const replies = repliesByParent[c.id] || [];
                return (
                  <li key={c.id}>
                    <CommentBlock c={c} onReply={() => setReplyTo({ id: c.id, userName: c.userName })} onDelete={deleteComment} onReport={setReportingId} viewerId={user?.id} />
                    {replies.length > 0 && (
                      <ul className="mt-2 ml-9 space-y-2 border-l border-stone-200 pl-3">
                        {replies.map((r) => (
                          <li key={r.id}>
                            <CommentBlock c={r} muted onReply={() => setReplyTo({ id: c.id, userName: c.userName })} onDelete={deleteComment} onReport={setReportingId} viewerId={user?.id} />
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Composer */}
        <div className="border-t border-stone-200 p-3">
          {replyTo && (
            <div className="mb-2 flex items-center justify-between text-[11px] text-stone-500 px-1">
              <span>Replying to <b className="text-stone-700">{replyTo.userName}</b></span>
              <button
                type="button"
                onClick={() => setReplyTo(null)}
                className="text-[10px] uppercase tracking-wide text-stone-400 hover:text-stone-600"
                data-testid="circle-cancel-reply"
              >
                cancel
              </button>
            </div>
          )}
          <div className="flex items-end gap-2">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={replyTo ? `Reply to ${replyTo.userName}…` : 'Add your voice…'}
              rows={2}
              maxLength={1000}
              className="flex-1 px-3 py-2 rounded-2xl text-[13px] leading-relaxed resize-none bg-white text-stone-800 placeholder:text-stone-400 border border-stone-200 focus:outline-none focus:ring-2 focus:ring-[#D6AD60]/50"
              data-testid="circle-comment-input"
            />
            <button
              type="button"
              onClick={submit}
              disabled={!draft.trim() || posting}
              data-testid="circle-comment-submit"
              className="rbr-btn-gold px-4 py-2.5 text-[12px] flex-shrink-0"
            >
              {posting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            </button>
          </div>
          <div className="mt-2"><IdentityToggle anonymous={anon} onChange={setAnon} name={user?.displayName || user?.name} testId="circle-comment-anon" verb="Reply" /></div>
        </div>
      </div>

      <ReportCommentModal
        open={!!reportingId}
        commentId={reportingId}
        onClose={() => setReportingId(null)}
      />
    </div>
  );
};

const CommentBlock = ({ c, muted, onReply, onDelete, onReport, viewerId }) => {
  const canDel = !!c.canDelete;
  const isMod  = canDel && !c.mine;   // author-and-canDel is normal; canDel-and-not-mine ⇒ admin
  // Show Report only when the viewer is signed in AND it's not their own
  // comment. If they can delete it (author or admin) they don't need to
  // report — they can just remove it.
  const showReport = !!viewerId && !c.mine && !canDel;
  return (
    <div className={`flex gap-2.5 ${muted ? 'opacity-90' : ''}`} data-testid={`circle-comment-${c.id}`}>
      {c.userAvatar ? (
        <img src={c.userAvatar} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
      ) : (
        <div className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-[10px] font-semibold text-[#242824]"
             style={{ background: 'linear-gradient(135deg,#D6AD60 0%,#AA8347 100%)' }}>
          {initials(c.userName)}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[12px] font-semibold text-stone-800 truncate">{c.userName}</span>
          {c.anonymous && c.mine && (
            <span className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#D6AD60]/25 text-[#5b4727]">
              anon · you
            </span>
          )}
          <span className="text-[10px] text-stone-400">{timeAgo(c.createdAt)}</span>
          {c.kind === 'encouragement' && (
            <span className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#F7F3EA] text-[#5b4727]">
              encouragement
            </span>
          )}
        </div>
        <p className="text-[13px] leading-relaxed text-stone-700 mt-0.5 whitespace-pre-line">
          {c.content}
        </p>
        <div className="flex items-center gap-3 mt-1">
          <button
            type="button"
            onClick={onReply}
            className="inline-flex items-center gap-1 text-[11px] text-stone-400 hover:text-stone-700 transition-colors"
            data-testid={`circle-reply-btn-${c.id}`}
          >
            <CornerUpRight className="w-3 h-3" /> Reply
          </button>
          {canDel && (
            <button
              type="button"
              onClick={() => onDelete?.(c.id, isMod)}
              className={`inline-flex items-center gap-1 text-[11px] transition-colors ${isMod ? 'text-orange-500 hover:text-orange-700' : 'text-stone-400 hover:text-red-500'}`}
              data-testid={`circle-comment-delete-${c.id}`}
              title={isMod ? 'Remove as moderator' : 'Delete'}
            >
              {isMod ? <ShieldAlert className="w-3 h-3" /> : <Trash2 className="w-3 h-3" />}
              {isMod ? 'Remove' : 'Delete'}
            </button>
          )}
          {showReport && (
            <button
              type="button"
              onClick={() => onReport?.(c.id)}
              className="inline-flex items-center gap-1 text-[11px] text-stone-400 hover:text-orange-500 transition-colors"
              data-testid={`circle-comment-report-${c.id}`}
              title="Report to moderators"
            >
              <Flag className="w-3 h-3" /> Report
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CircleCommentSheet;
